import React, { useState } from 'react';
import {
  Box, TextField, Button, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, IconButton, InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { tenantAPI } from '../../services/api';

const ProvisionDialog = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    school_name: '', tenant_code: '', admin_name: '', admin_email: '', admin_phone: '', admin_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.school_name || !form.tenant_code || !form.admin_name || !form.admin_email || !form.admin_password) {
      setError('All required fields must be filled');
      return;
    }
    if (form.admin_password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await tenantAPI.provision(form);
      onSuccess({
        schoolId: form.tenant_code,
        email: form.admin_email,
        password: form.admin_password,
      });
      setForm({ school_name: '', tenant_code: '', admin_name: '', admin_email: '', admin_phone: '', admin_password: '' });
      setShowPassword(false);
    } catch (err) {
      const d = err.response?.data;
      if (d?.errors) setError(Object.values(d.errors).flat().join(', '));
      else setError(d?.message || 'Failed to add school');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setForm({ school_name: '', tenant_code: '', admin_name: '', admin_email: '', admin_phone: '', admin_password: '' });
    setShowPassword(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New School</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
        <Box component="form" id="provision-form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField fullWidth label="School Name" required margin="normal" value={form.school_name}
            onChange={(e) => setForm({ ...form, school_name: e.target.value })} />
          <TextField fullWidth label="School ID" required margin="normal" value={form.tenant_code}
            onChange={(e) => setForm({ ...form, tenant_code: e.target.value.replace(/[^A-Za-z0-9_-]/g, '') })}
            helperText="Letters, numbers, hyphens and underscores only" />
          <TextField fullWidth label="Admin Name" required margin="normal" value={form.admin_name}
            onChange={(e) => setForm({ ...form, admin_name: e.target.value })} />
          <TextField fullWidth label="Admin Email" type="email" required margin="normal" value={form.admin_email}
            onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
          <TextField fullWidth label="Admin Phone (optional)" margin="normal" value={form.admin_phone}
            onChange={(e) => setForm({ ...form, admin_phone: e.target.value })} />
          <TextField fullWidth label="Admin Password" type={showPassword ? 'text' : 'password'} required margin="normal"
            value={form.admin_password}
            onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button type="submit" form="provision-form" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Add School'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProvisionDialog;
